import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import {
  FileDownloadOutlined as ExportIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../../components";
import { useAuth } from "../../../contexts/auth";
import { baseAxios, Context } from "../../../utils/config";

const API = "gateway/cs86dhgzsi"; // Development API
// const API = "gateway/qcwiey5h1j"; // Production API

const dateFieldSx = {
  minWidth: 160,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "background.paper",
    borderRadius: "6px",
  },
};

function formatDateToISO(date) {
  if (!date || !dayjs(date).isValid()) return "";
  return dayjs(date).format("YYYY-MM-DD");
}

function validateDateRange(startDate, endDate) {
  if (
    !startDate ||
    !endDate ||
    !dayjs(startDate).isValid() ||
    !dayjs(endDate).isValid()
  ) {
    toast.error("Please select both start and end dates", { duration: 3000 });
    return false;
  }
  if (dayjs(startDate).isAfter(dayjs(endDate), "day")) {
    toast.error("Start date must be before or equal to end date", {
      duration: 3000,
    });
    return false;
  }
  return true;
}

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (typeof va === "number" || typeof vb === "number") {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

/**
 * Salon Container Usage — product usage totals for a salon over a date range
 */
export default function SalonReports() {
  const { user } = useAuth();
  const { location: globalLocation } = useContext(Context);

  const [startDate, setStartDate] = useState(dayjs().subtract(7, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // The last load failed; an empty table then means "unknown", not "no data".
  const [loadFailed, setLoadFailed] = useState(false);
  // Counts the loads. Typing a date starts one load per keystroke, and an
  // earlier, slower answer must not replace the newest one.
  const latestLoad = useRef(0);
  const [hasRun, setHasRun] = useState(false);
  const [sortBy, setSortBy] = useState("container_used");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadReport = useCallback(async () => {
    if (!validateDateRange(startDate, endDate)) return;

    const thisLoad = ++latestLoad.current;
    setLoading(true);
    setLoadFailed(false);
    setHasRun(true);
    try {
      const payload = {
        Barcode: user.barcode,
        Role: user.userRoles,
        StartDate: dayjs(startDate).toDate(),
        EndDate: dayjs(endDate).toDate(),
        Salon: {
          location_id:
            globalLocation?.location_id !== -1
              ? globalLocation?.location_id
              : user.location_id,
          location_name:
            globalLocation?.location_name || user.location_name,
        },
      };

      const result = await baseAxios.post(API, payload);
      if (thisLoad !== latestLoad.current) return;
      const data = Array.isArray(result.data)
        ? result.data
        : Array.isArray(result.data?.data)
          ? result.data.data
          : [];
      setRows(data);
    } catch (error) {
      if (thisLoad !== latestLoad.current) return;
      setRows([]);
      setLoadFailed(true);
      // baseAxios rejects with { status, data }, not with an axios error.
      if ((error?.status ?? error?.response?.status) === 403) {
        toast.error("Not allowed to see this data", { duration: 3000 });
      } else {
        toast.error("Error loading data", { duration: 3000 });
      }
    } finally {
      if (thisLoad === latestLoad.current) setLoading(false);
    }
  }, [endDate, globalLocation, startDate, user]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [rows, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const handleExport = useCallback(async () => {
    if (!sortedRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }

    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Salon Container Usage");
      worksheet.columns = [
        { header: "Salon", key: "location_name", width: 22 },
        { header: "Product ID", key: "product_alias", width: 28 },
        { header: "Container Used", key: "container_used", width: 16 },
      ];
      worksheet.getRow(1).font = { bold: true };
      sortedRows.forEach((row) =>
        worksheet.addRow({
          location_name: row.location_name,
          product_alias: row.product_alias,
          container_used: Number(row.container_used) || 0,
        })
      );

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `SalonContainerUsage_${formatDateToISO(startDate)}_${formatDateToISO(endDate)}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [endDate, sortedRows, startDate]);

  const columns = useMemo(
    () => [
      {
        id: "location_name",
        label: "Salon",
        field: "location_name",
        minWidth: 180,
        render: (v) => v || "—",
      },
      {
        id: "product_alias",
        label: "Product ID",
        field: "product_alias",
        minWidth: 220,
        render: (v) => v || "—",
      },
      {
        id: "container_used",
        label: "Container Used",
        field: "container_used",
        minWidth: 150,
        align: "left",
        render: (v) => {
          const n = Number(v);
          if (Number.isNaN(n)) return v ?? "—";
          return n % 1 === 0 ? String(n) : n.toFixed(2);
        },
      },
    ],
    []
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ px: 8, py: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mt: 1.5,
            mb: 2,
          }}
        >
          <Box>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              Salon Container Usage
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total amount of product used at a salon over a date range, broken
              down by product.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={(value) => setStartDate(value)}
              slotProps={{ textField: { size: "small", sx: dateFieldSx } }}
            />
            <DatePicker
              label="End date"
              value={endDate}
              onChange={(value) => setEndDate(value)}
              maxDate={dayjs()}
              slotProps={{ textField: { size: "small", sx: dateFieldSx } }}
            />
            <IconButton
              aria-label="Refresh report"
              onClick={loadReport}
              disabled={loading}
              size="medium"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                borderRadius: "6px",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              <RefreshIcon />
            </IconButton>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              disabled={loading || !sortedRows.length}
              sx={{
                minHeight: 40,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "6px",
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={loadReport}
              disabled={loading}
              sx={{
                minHeight: 40,
                px: 2,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "6px",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {loading ? "Loading…" : "Run Report"}
            </Button>
          </Box>
        </Box>

        <PaginatedTable
          columns={columns}
          rows={sortedRows}
          totalRowCount={sortedRows.length}
          loading={loading}
          onPaginationChange={noopPagination}
          onSortChange={handleSortChange}
          initialOrderBy="container_used"
          initialOrder="desc"
          searchQuery={`${formatDateToISO(startDate)}|${formatDateToISO(endDate)}|${globalLocation?.location_id ?? "all"}`}
          clientSidePagination
          skeletonHeight="40px"
          getRowKey={(row, index) =>
            `${row.location_name || "salon"}-${row.product_alias || "product"}-${index}`
          }
          emptyMessage={
            loadFailed
              ? "Could not load this report. Try again."
              : hasRun
              ? "No container usage data for this date range"
              : "Click Run Report to load data"
          }
        />
      </Box>
    </LocalizationProvider>
  );
}
